import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { toast } from "sonner";
import { Linkedin, Plus, Trash2, Play, Pause, CheckCheck, Inbox, Zap, Settings2, X, ArrowRightLeft } from "lucide-react";

const STATUS = {
  active: ["ACTIVE", "#2F7A3E"], draft: ["DRAFT", "#E2711D"],
  paused: ["PAUSED", "#94A3AD"], completed: ["COMPLETED", "#3E8F99"],
};
const PROVIDER = {
  closely: { label: "Closely · manual", color: "#3E8F99" },
  heyreach: { label: "HeyReach · automated", color: "#7A5CB2" },
};
const STEP_TYPES = ["connect", "message", "inmail"];

function AccountsPanel({ data, onChange, isAdmin }) {
  const [showAdd, setShowAdd] = useState(false);
  const [f, setF] = useState({ name: "", provider: "closely", heyreach_account_id: "" });
  const add = async () => {
    if (!f.name.trim()) { toast.error("Account name required"); return; }
    try { await api.post("/linkedin/accounts", f); setShowAdd(false); setF({ name: "", provider: "closely", heyreach_account_id: "" }); onChange(); toast.success("Account added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reassign = async (a, provider) => {
    try { await api.patch(`/linkedin/accounts/${a.id}`, { provider }); onChange(); toast.success(`${a.name} → ${provider}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (a) => {
    if (!window.confirm(`Remove "${a.name}"?`)) return;
    try { await api.delete(`/linkedin/accounts/${a.id}`); onChange(); toast.success("Removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [syncing, setSyncing] = useState(false);
  const syncHeyreach = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/linkedin/accounts/sync-heyreach");
      const lines = (r.discovered || []).map((d) => `${d.name} · ${d.is_active ? "active" : "inactive"} · ${d.auth_valid ? "auth valid" : "auth INVALID"} · id ${d.heyreach_account_id}`).join("\n");
      onChange();
      toast.success(`HeyReach: ${(r.discovered || []).length} account(s) found · ${r.seats_updated} seat ID(s) auto-filled`, { description: lines || "No LinkedIn accounts connected in HeyReach yet" });
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="li-accounts">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Connected accounts · provider assignment</div>
          <div className="text-xs text-[#1C2530]/50 mt-0.5">Each account runs on exactly one tool — reassign anytime{!data.heyreach_configured && " · HeyReach key not yet in Vault"}</div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            {data.heyreach_configured && <button onClick={syncHeyreach} disabled={syncing} data-testid="sync-heyreach-btn" className="flex items-center gap-1 border border-[#DFDAD0] px-2.5 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5] disabled:opacity-50">{syncing ? "Syncing…" : "Sync from HeyReach"}</button>}
            <button onClick={() => setShowAdd(true)} data-testid="add-li-account-btn" className="flex items-center gap-1 border border-[#DFDAD0] px-2.5 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Plus className="w-3 h-3" /> Account</button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {data.accounts.map((a) => {
          const p = PROVIDER[a.provider];
          return (
            <div key={a.id} className="border border-[#DFDAD0] p-3 flex items-center justify-between" data-testid={`li-account-${a.id}`} style={{ borderLeft: `3px solid ${p.color}` }}>
              <div>
                <div className="text-sm font-medium text-[#1C2530]">{a.name}</div>
                <div className="font-mono text-[9px] uppercase mt-0.5" style={{ color: p.color }}>{p.label} · {a.active_campaigns} active</div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <select value={a.provider} onChange={(e) => reassign(a, e.target.value)} data-testid={`li-account-provider-${a.id}`} className="border border-[#DFDAD0] px-2 py-1 text-xs bg-white outline-none">
                    {data.providers.map((pr) => <option key={pr} value={pr}>{pr}</option>)}
                  </select>
                  <button onClick={() => del(a)} data-testid={`li-account-del-${a.id}`} className="text-[#B23A2E] p-1.5 hover:bg-[#B23A2E]/5"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-li-account-modal">
          <div className="bg-white border border-[#DFDAD0] w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-[#1C2530]">Connect account</h3><button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-[#1C2530]/50" /></button></div>
            <div className="space-y-3">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Account label" data-testid="li-account-name" className="w-full border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
              <select value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} data-testid="li-account-provider" className="w-full border border-[#DFDAD0] px-3 py-2 text-sm bg-white outline-none">
                {data.providers.map((pr) => <option key={pr} value={pr}>{pr}</option>)}
              </select>
              {f.provider === "heyreach" && <input value={f.heyreach_account_id} onChange={(e) => setF({ ...f, heyreach_account_id: e.target.value })} placeholder="HeyReach account ID (optional)" data-testid="li-account-heyreach-id" className="w-full border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />}
              <button onClick={add} data-testid="li-account-save" className="w-full bg-[#1C2530] text-[#F5F3EE] py-2.5 text-sm font-medium hover:bg-[#2a3644]">Add account</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Designer({ accounts, onCreate }) {
  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState(accounts[0]?.id || "");
  const [aud, setAud] = useState({ industry: "", geography: "India", size: "", titles: "" });
  const [steps, setSteps] = useState([{ type: "connect", delay_days: 0, message: "" }]);
  const setStep = (i, k, v) => setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, [k]: v } : st)));
  const acct = accounts.find((a) => a.id === accountId);

  const submit = () => {
    if (!name.trim()) { toast.error("Campaign name required"); return; }
    if (!accountId) { toast.error("Pick a LinkedIn account"); return; }
    onCreate({
      name, account_id: accountId,
      audience: { ...aud, titles: aud.titles.split(",").map((t) => t.trim()).filter(Boolean) },
      steps: steps.map((s) => ({ ...s, delay_days: +s.delay_days })),
      region: "India", start_hour: 9, end_hour: 18,
    });
    setName(""); setSteps([{ type: "connect", delay_days: 0, message: "" }]);
  };

  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="li-designer">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Campaign Designer · routes to the account's tool</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="li-name" placeholder="Campaign name" className="border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="li-account-select" className="border border-[#DFDAD0] px-3 py-2 text-sm bg-white outline-none focus:border-[#1C2530]">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.provider}</option>)}
        </select>
      </div>
      {acct && <div className="mb-3 text-xs px-3 py-2" style={{ background: `${PROVIDER[acct.provider].color}12`, color: PROVIDER[acct.provider].color }}>
        {acct.provider === "heyreach" ? "Automated — created & started live via HeyReach API." : "Manual — record here, then run natively in Closely & import replies via Bulk CSV."}
      </div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <input value={aud.industry} onChange={(e) => setAud({ ...aud, industry: e.target.value })} data-testid="li-industry" placeholder="Industry" className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
        <select value={aud.geography} onChange={(e) => setAud({ ...aud, geography: e.target.value })} data-testid="li-geo" className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
          {["India", "UAE", "Qatar", "GCC"].map((g) => <option key={g}>{g}</option>)}
        </select>
        <input value={aud.size} onChange={(e) => setAud({ ...aud, size: e.target.value })} placeholder="Size" className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
        <input value={aud.titles} onChange={(e) => setAud({ ...aud, titles: e.target.value })} data-testid="li-titles" placeholder="Titles (comma)" className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
      </div>
      <div className="space-y-2 mb-3" data-testid="li-steps">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-2 items-start">
            <select value={s.type} onChange={(e) => setStep(i, "type", e.target.value)} data-testid={`li-step-type-${i}`} className="border border-[#DFDAD0] px-2 py-1.5 text-xs bg-white outline-none w-24">
              {STEP_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <input type="number" min="0" value={s.delay_days} onChange={(e) => setStep(i, "delay_days", e.target.value)} title="delay days" className="border border-[#DFDAD0] px-2 py-1.5 text-xs w-16 outline-none focus:border-[#1C2530]" />
            <input value={s.message} onChange={(e) => setStep(i, "message", e.target.value)} placeholder="Message / note" className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-xs outline-none focus:border-[#1C2530]" />
            {steps.length > 1 && <button onClick={() => setSteps((st) => st.filter((_, idx) => idx !== i))} className="text-[#B23A2E] p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => setSteps((s) => [...s, { type: "message", delay_days: 2, message: "" }])} data-testid="li-add-step" className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Plus className="w-3 h-3" /> Add step</button>
        <button onClick={submit} data-testid="li-create-btn" className="flex items-center gap-2 bg-[#E50914] text-white px-4 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#c40810] ml-auto"><Linkedin className="w-3.5 h-3.5" /> Launch Campaign</button>
      </div>
    </div>
  );
}

function RepliesDrawer({ cid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/linkedin/campaigns/${cid}/replies`).then((r) => setData(r.data)).catch((e) => { toast.error(e.response?.data?.detail || "Failed"); setData({ error: true }); }); }, [cid]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/50" onClick={onClose} data-testid="li-replies-drawer">
      <div className="bg-[#F5F3EE] w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-[#1C2530] flex items-center gap-2"><Inbox className="w-5 h-5" /> Replies</h3><button onClick={onClose}><X className="w-5 h-5 text-[#1C2530]/50" /></button></div>
        {!data ? <Loading /> : data.error ? <div className="text-sm text-[#B23A2E]">Could not load replies.</div> : (
          <>
            {data.live?.manual && <div className="text-xs bg-[#3E8F99]/10 text-[#3E8F99] px-3 py-2 mb-3">{data.live.note}</div>}
            {(data.stored_replies || []).length === 0 ? <Empty label="NO_REPLIES_YET" /> : data.stored_replies.map((r, i) => (
              <div key={i} className="border-l-2 border-[#DFDAD0] pl-3 py-2 mb-2"><div className="font-mono text-[9px] uppercase text-[#1C2530]/50">{r.from} · {new Date(r.at).toLocaleString()}</div><div className="text-sm text-[#1C2530] mt-0.5">{r.text}</div></div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default function LinkedInAgent() {
  const { user } = useAuth();
  const [accts, setAccts] = useState(null);
  const [camps, setCamps] = useState(null);
  const [replies, setReplies] = useState(null);
  const isAdmin = user.role === "admin";

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/linkedin/accounts"), api.get("/linkedin/campaigns")]);
    setAccts(a.data); setCamps(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (body) => {
    try { const r = await api.post("/linkedin/campaigns", body); toast.success(r.data._note || "Campaign created"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = async (id, action) => {
    try { const r = await api.post(`/linkedin/campaigns/${id}/${action}`); toast.success(r.data.note || action); load(); }
    catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); }
  };

  return (
    <>
      <PageHeader eyebrow="PROVIDER ABSTRACTION · CLOSELY (MANUAL) + HEYREACH (AUTOMATED)" title="LinkedIn Agent" hint="Run LinkedIn campaigns: Closely for manual, human-paced steps; HeyReach for automated sequences. Campaigns are queued until an Admin activates them." />
      <div className="p-8 space-y-6" data-testid="linkedin-agent-page">
        {!accts ? <Loading /> : <AccountsPanel data={accts} onChange={load} isAdmin={isAdmin} />}
        {accts && accts.accounts.length > 0 && <Designer accounts={accts.accounts} onCreate={create} />}

        {!camps ? <Loading /> : camps.length === 0 ? <Empty label="NO_CAMPAIGNS" /> : (
          <div className="space-y-3" data-testid="li-campaigns">
            {camps.map((c) => {
              const [sl, sc] = STATUS[c.status] || [c.status, "#94A3AD"];
              const p = PROVIDER[c.provider] || { label: c.provider, color: "#94A3AD" };
              return (
                <div key={c.id} className="border border-[#DFDAD0] bg-white p-4" data-testid={`li-campaign-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#1C2530]">{c.name}</span>
                        <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 border" style={{ color: sc, borderColor: sc }}>{sl}</span>
                        <span className="font-mono text-[9px] uppercase px-1.5 py-0.5" style={{ color: p.color, background: `${p.color}15` }}>{p.label}</span>
                        <span className="font-mono text-[9px] text-[#1C2530]/40">{c.account_name}</span>
                      </div>
                      <div className="font-mono text-[11px] text-[#1C2530]/55 mt-1">{c.audience?.industry || "any"} · {c.audience?.geography} · {c.steps?.length} steps · by {c.created_by}</div>
                      <div className="flex gap-4 mt-2 font-mono text-[11px] text-[#1C2530]/70">
                        <span>sent <b className="tabular-nums">{c.stats?.sent || 0}</b></span>
                        <span>accepted <b className="tabular-nums">{c.stats?.accepted || 0}</b></span>
                        <span>replied <b className="tabular-nums">{c.stats?.replied || 0}</b></span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap justify-end">
                      <button onClick={() => setReplies(c.id)} data-testid={`li-replies-${c.id}`} className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Inbox className="w-3 h-3" /> Replies</button>
                      {c.status === "draft" && <button onClick={() => act(c.id, "start")} data-testid={`li-start-${c.id}`} className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#2a3644]"><Play className="w-3 h-3" /> Start</button>}
                      {c.status === "active" && <>
                        <button onClick={() => act(c.id, "pause")} data-testid={`li-pause-${c.id}`} className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Pause className="w-3 h-3" /> Pause</button>
                        <button onClick={() => act(c.id, "complete")} className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><CheckCheck className="w-3 h-3" /> Done</button>
                      </>}
                      {c.status === "paused" && <button onClick={() => act(c.id, "start")} data-testid={`li-resume-${c.id}`} className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#2a3644]"><Play className="w-3 h-3" /> Resume</button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {replies && <RepliesDrawer cid={replies} onClose={() => setReplies(null)} />}
    </>
  );
}
